import { z } from 'zod';
import { TCCredentialKind, TCCredentialStatus } from 'src/api/generated';
import {
  getForcedCredentialKindForProvider,
  OTHER_CREDENTIAL_PROVIDER_OPTION,
} from 'src/modules/settings/utils/credentials.util';

const isJsonObjectString = (value: string): boolean => {
  try {
    const parsed: unknown = JSON.parse(value);
    return (
      typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
    );
  } catch {
    return false;
  }
};

/**
 * Validation schema for the create credential form.
 */
export const credentialCreateFormSchema = z
  .object({
    /** Provider selected from the supported list (or `other`). */
    providerOption: z.string().min(1, 'Requerido'),
    /** Provider identifier used when `providerOption` is `other`. */
    otherProvider: z.string().optional(),
    /** Display name for the credential. */
    name: z.string().min(1, 'Requerido'),
    /** Credential kind. */
    kind: z.nativeEnum(TCCredentialKind),
    /** Credential status. */
    status: z.nativeEnum(TCCredentialStatus),
    /** Whether this credential can be used by other users in the tenant. */
    sharedWithTenant: z.boolean().default(false),
    /** API key value (kind=apiKey). */
    apiKey: z.string().optional(),
    /** Bearer token value (kind=bearer). */
    bearerToken: z.string().optional(),
    /** Basic auth username (kind=basic). */
    basicUsername: z.string().optional(),
    /** Basic auth password (kind=basic). */
    basicPassword: z.string().optional(),
    /** JSON string representing a custom secret object (kind=custom). */
    customSecretJson: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    if (
      values.providerOption === OTHER_CREDENTIAL_PROVIDER_OPTION &&
      (!values.otherProvider || values.otherProvider.trim().length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherProvider'],
        message: 'Requerido',
      });
    }

    const forcedKind = getForcedCredentialKindForProvider(
      values.providerOption
    );
    if (forcedKind && values.kind !== forcedKind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['kind'],
        message: 'Tipo inválido para este proveedor',
      });
    }

    switch (values.kind) {
      case TCCredentialKind.API_KEY:
        if (!values.apiKey || values.apiKey.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['apiKey'],
            message: 'Requerido',
          });
        }
        break;
      case TCCredentialKind.BEARER:
        if (!values.bearerToken || values.bearerToken.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bearerToken'],
            message: 'Requerido',
          });
        }
        break;
      case TCCredentialKind.BASIC:
        if (!values.basicUsername || values.basicUsername.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['basicUsername'],
            message: 'Requerido',
          });
        }
        if (!values.basicPassword || values.basicPassword.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['basicPassword'],
            message: 'Requerido',
          });
        }
        break;
      case TCCredentialKind.CUSTOM:
        if (
          !values.customSecretJson ||
          values.customSecretJson.trim().length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customSecretJson'],
            message: 'Requerido',
          });
        } else if (!isJsonObjectString(values.customSecretJson)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customSecretJson'],
            message: 'Debe ser un objeto JSON',
          });
        }
        break;
      default:
        break;
    }
  });

/**
 * Form values for the create credential form.
 */
export type CredentialCreateFormValues = z.infer<
  typeof credentialCreateFormSchema
>;

/**
 * Input values accepted by the create credential schema before Zod transforms/defaults.
 */
export type CredentialCreateFormInputValues = z.input<
  typeof credentialCreateFormSchema
>;

/**
 * Validation schema for the edit credential form.
 */
export const credentialEditFormSchema = z.object({
  /** Display name for the credential. */
  name: z.string().min(1, 'Requerido'),
  /** Credential status. */
  status: z.nativeEnum(TCCredentialStatus),
  /** Whether this credential can be used by other users in the tenant. */
  sharedWithTenant: z.boolean(),
});

/**
 * Form values for the edit credential form.
 */
export type CredentialEditFormValues = z.infer<typeof credentialEditFormSchema>;

/**
 * Creates a validation schema for the rotate secret form (kind-specific).
 *
 * @param kind - Credential kind.
 * @returns Zod schema for rotate secret form.
 */
export const buildCredentialRotateSecretFormSchema = (kind: TCCredentialKind) =>
  z
    .object({
      /** API key value (kind=apiKey). */
      apiKey: z.string().optional(),
      /** Bearer token value (kind=bearer). */
      bearerToken: z.string().optional(),
      /** Basic auth username (kind=basic). */
      basicUsername: z.string().optional(),
      /** Basic auth password (kind=basic). */
      basicPassword: z.string().optional(),
      /** JSON string representing a custom secret object (kind=custom). */
      customSecretJson: z.string().optional(),
    })
    .superRefine((values, ctx) => {
      switch (kind) {
        case TCCredentialKind.API_KEY:
          if (!values.apiKey || values.apiKey.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['apiKey'],
              message: 'Requerido',
            });
          }
          break;
        case TCCredentialKind.BEARER:
          if (!values.bearerToken || values.bearerToken.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['bearerToken'],
              message: 'Requerido',
            });
          }
          break;
        case TCCredentialKind.BASIC:
          if (
            !values.basicUsername ||
            values.basicUsername.trim().length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['basicUsername'],
              message: 'Requerido',
            });
          }
          if (
            !values.basicPassword ||
            values.basicPassword.trim().length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['basicPassword'],
              message: 'Requerido',
            });
          }
          break;
        case TCCredentialKind.CUSTOM:
          if (
            !values.customSecretJson ||
            values.customSecretJson.trim().length === 0
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['customSecretJson'],
              message: 'Requerido',
            });
          } else if (!isJsonObjectString(values.customSecretJson)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['customSecretJson'],
              message: 'Debe ser un objeto JSON',
            });
          }
          break;
        default:
          break;
      }
    });

/**
 * Form values for the rotate secret form (kind-independent shape).
 */
export type CredentialRotateSecretFormValues = z.infer<
  ReturnType<typeof buildCredentialRotateSecretFormSchema>
>;
